import { useState, useCallback, useEffect } from 'react'
import { useSelector } from '../../hooks/useSelector'
import firebase from 'firebase/app'
import useGames from './hooks/useGames'
import { CreateGameModal } from './modals/CreateGameModal'
import useRouting from '../../hooks/useRouting'
import { useJoinLobby } from '../../hooks/useJoinLobby'
import { Stack, Typography, Card, Button, Table } from '@prometheus/components'
import PromiseButtonV2 from '@/components/PromiseButtonV2'
import { EditProfileModal } from '@/components/EditProfileModal'
import { GuestLinkModal } from '@/components/GuestLinkModal'
import { useDispatch } from 'react-redux'
import { resetState } from '@/reducers/game/game'
import { useGuestSession } from '@/hooks/useGuestSession'
import { LobbyIdentity } from './LobbyIdentity'

const provider = new firebase.auth.GoogleAuthProvider()
if (window.location.host.indexOf('localhost') !== -1) {
  firebase.auth().setPersistence(firebase.auth.Auth.Persistence.SESSION)
}

export default () => {
  const authorized = useSelector(s => s.auth.authorized)
  const signedIn = useSelector(s => s.auth.signedIn)
  const guest = useSelector(s => s.auth.guest)
  const guestToken = useSelector(s => s.auth.guestToken)
  const displayName = useSelector(s => s.auth.displayName)
  const uid = useSelector(s => s.auth.uid)
  const admin = useSelector(s => s.auth.admin)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingProfile, setEditingProfile] = useState(false)
  const [playLinkOpen, setPlayLinkOpen] = useState(false)
  const [justStartedAsGuest, setJustStartedAsGuest] = useState(false)
  const [startingGuest, setStartingGuest] = useState(false)
  const [guestError, setGuestError] = useState<string | null>(null)
  const { startGuest } = useGuestSession()
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(resetState())
  }, [])

  const { push } = useRouting()

  const {
    games,
    onCreate,
    onJoin,
  } = useGames()
  
  const onSignIn = useCallback(() => {
    firebase.auth().signInWithPopup(provider)
  }, [])

  // Straight into a game, no account. The play link opens immediately after:
  // it's the only way back to whatever they're about to start, so the moment
  // they get an identity is the moment to hand it over.
  const onPlayAsGuest = useCallback(async () => {
    setStartingGuest(true)
    setGuestError(null)

    try {
      await startGuest()
      setJustStartedAsGuest(true)
      setPlayLinkOpen(true)
    } catch (e) {
      setGuestError((e as Error)?.message || 'Could not start a guest session.')
    } finally {
      setStartingGuest(false)
    }
  }, [startGuest])

  const onShowPlayLink = useCallback(() => {
    setJustStartedAsGuest(false)
    setPlayLinkOpen(true)
  }, [])

  const onSubmit = useCallback((name: string, tutorial?: boolean) => {
    return onCreate(name, tutorial)
      .then((r) => {
        push(`/games/${r.data}`)()
        setModalOpen(false)
      })
      .catch((e) => {
        console.error(e)
      })
  }, [onCreate, push])

  useJoinLobby()

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', boxSizing: 'border-box', overflowY: 'auto' }}>
      <Card borderColor='primary' style={{ width: '100%', maxWidth: '512px' }}>
        <Stack style={{ alignItems: 'center' }}>
          <Stack space="xs">
            <Typography align='center' variant='h1' style={{ fontSize: 'clamp(1.9rem, 11.5vw, 3.75rem)', lineHeight: 1, letterSpacing: '0.02em', textShadow: '0 0 18px var(--prometheus-primary)' }} retro color='primary'>PROMETHEUS</Typography>
            {admin && (
              <Card style={{ width: '180px', margin: '0 auto' }} center padding="none" borderColor="warning">
                <Typography color="warning" variant="h4" retro component="span">ADMINISTRATOR</Typography>
              </Card>
            )}
            <LobbyIdentity
              signedIn={signedIn}
              guest={guest}
              displayName={displayName}
              busy={startingGuest}
              error={guestError}
              onSignIn={onSignIn}
              onPlayAsGuest={onPlayAsGuest}
              onEditProfile={() => setEditingProfile(true)}
              onShowPlayLink={onShowPlayLink}
            />
          </Stack>
          {authorized && (
            <div style={{ display: 'flex', width: '100%', flexDirection: 'row-reverse', gap: '8px' }}>
              <Button outline size="sm" disabled={!authorized} onClick={() => setModalOpen(true)}>Create Game</Button>
              <Button outline size="sm" variant="secondary" disabled={!authorized}>Load Game</Button>
            </div>
          )}
          <Table
            hoverable
            columns={[
              { key: 'name', label: 'Name' },
              { key: 'players', label: 'Players' },
              { key: 'action', label: 'Actions', align: 'right' },
            ]}
            data={games.filter(g => !g.tutorial).map((game) => {
              return {
                name: game.name,
                players: game.players.length,
                action: (() => {
                  const isJoined = !!game.players.find(p => p.id === uid)
                  const isStarted = game.status !== 'new'
                  const isLoading = game.status === 'loading'
                  if (isLoading) {
                    return <PromiseButtonV2 type="button" variant="tertiary" size="sm" onClick={push(`/games/resume/${game.id}`)}>Resume</PromiseButtonV2>
                  }
                  if (isJoined) {
                    return <PromiseButtonV2 type="button" variant="warning" size="sm" onClick={push(`/games/${game.id}`)}>View</PromiseButtonV2>
                  } else if (isStarted && !isJoined) {
                    return <Button variant="info" disabled size="sm">In Progress</Button>
                  } else {
                    return <PromiseButtonV2 type="button" variant="primary" onClick={() => onJoin(game.id).then(r => push(`/games/${r.data}`)())} disabled={!authorized} size="sm" color="green">Join Game</PromiseButtonV2>
                  }
                })()
              }
            })}
          />
        </Stack>
        
        <CreateGameModal
          onSubmit={onSubmit}
          setOpen={setModalOpen}
          open={modalOpen}
        />
        <EditProfileModal open={editingProfile} onCancel={() => setEditingProfile(false)} />
        <GuestLinkModal
          open={playLinkOpen}
          token={guestToken}
          introduction={justStartedAsGuest}
          onClose={() => setPlayLinkOpen(false)}
        />
      </Card>
    </div>
  )
}
